// scheduling/components/dashboard/JobsView.jsx
// Presentational: the manager jobs surface — sortable/filterable jobs table, detail
// sidebar, Schedule modal (person + placeholder), Roll-Forward wizard, Link-Job.
// Serves BOTH My Jobs (scope="mine") and All Jobs (scope="all") — one render path,
// matching the old ManagerDashboard tab==='my'/'all'. Shared job data + refresh
// callbacks arrive via props from the Dashboard owner; no fetching / me-resolution.

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { addDependency, removeDependency, rollForward as rollForwardAPI, setJobOnSite } from '../../services/schedApi';
import OnSiteToggle from '../OnSiteToggle';
import { BRAND, JOB_TYPE_COLORS, SCHED_STATUS, DEPT_GROUPS, normalizeDept, isPostReview, getJobPhase, PHASE_LABELS, jobDept } from '../../utils/schedConstants';
import { spreadWithinCapacity, MIN_PLANNED_DAY_HOURS } from '../../utils/optimizeHours';
import { toISO } from '../../utils/capacity';
import { computeJobStats } from '../../utils/managerJobData';
import ContractChip from '../ContractChip';
import SendToPoolModal from '../pool/SendToPoolModal';
import { POOL_META, isPoolableService } from '../../utils/poolEligibility';
import { fetchPoolConfig } from '../../services/poolApi';
import SchedStatBar from '../SchedStatBar';
import ClosedJobsSection from './ClosedJobsSection';
import { useTourDrive } from '../../../components/tour/tourStore';
import { SENTINEL_JOB_ID, TOUR_RF_PREVIEW } from '../../../components/tour/tourDemoData';

// ── Role colors + priority for the roll-forward Gantt ─────────────────────────
const ROLE_COLORS = {
  'Engagement Leader': '#0F766E', 'Reviewer': '#1D4ED8', 'Preparer': '#059669',
  'Proofer': '#4F46E5', 'Quality Control': '#7C3AED', 'QC Assign': '#7C3AED',
  'Manager': '#1D4ED8', 'Team Member': '#475569', 'Member': '#475569',
  'Admin Support': '#6B7280', 'Biller': '#6B7280', 'Interested Party': '#9CA3AF',
};
const ROLE_PRIORITY = ['Engagement Leader', 'Reviewer', 'Preparer', 'Proofer', 'Quality Control', 'QC Assign', 'Manager', 'Team Member', 'Member'];
function primaryRoleColor(roles) {
  for (const r of ROLE_PRIORITY) { if (roles.includes(r)) return ROLE_COLORS[r]; }
  return ROLE_COLORS[roles[0]] || '#475569';
}

function fmtDate(iso) {
  if (!iso) return '—';
  const s = typeof iso === 'string' ? iso.slice(0, 10) + 'T12:00:00' : iso;
  return new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
function fmtShortDate(iso) {
  if (!iso) return '';
  const s = typeof iso === 'string' ? iso.slice(0, 10) + 'T12:00:00' : iso;
  return new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
// Tooltip for the pool chip on a job row. Jobs keep their pool episode after it's
// claimed (so they stay out of My Jobs), so the chip must not keep claiming the job is
// "waiting" once someone has taken it.
function poolChipTitle(j) {
  const pool = POOL_META[j.PoolCode]?.label || j.PoolCode;
  return j.PoolState === 'claimed'
    ? `Claimed out of the ${pool} pool — tracking continues in Practice Engine`
    : `Waiting in the ${pool} pool since ${fmtShortDate(j.PooledAt)}`;
}
function getMonday(d) {
  const dt = new Date(d);
  const day = dt.getDay();
  dt.setDate(dt.getDate() - (day === 0 ? 6 : day - 1));
  dt.setHours(0, 0, 0, 0);
  return dt;
}
function addDaysLocal(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}
const roundQ = (n) => Math.round((Number(n) || 0) * 100) / 100;

// Roll Forward's default Job Start Week: the prior year's first scheduled week, one year
// on (52 weeks keeps it a Monday), so the PY offsets line up with the same weeks of the
// year. DJB's Jan–Jun 2027 instructions had to warn every JM that it defaulted to next
// week instead. Falls back to this week when the PY-aligned week has already passed.
function rfDefaultWeek(team) {
  const thisMonday = getMonday(new Date());
  const pyFirst = (team || []).reduce((min, m) => (!m.firstDate ? min : (!min || m.firstDate < min ? m.firstDate : min)), null);
  if (!pyFirst) return { week: thisMonday, hint: null };
  const pyMonday = getMonday(new Date(String(pyFirst).slice(0, 10) + 'T12:00:00'));
  const aligned = addDaysLocal(pyMonday, 364);
  return aligned >= thisMonday
    ? { week: aligned, hint: `Prior year started the week of ${fmtShortDate(toISO(pyMonday))}, so this defaults to the same week this year.` }
    : { week: thisMonday, hint: `Prior year started the week of ${fmtShortDate(toISO(pyMonday))}; that week has passed this year, so this defaults to this week.` };
}

const STATUS_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'unscheduled', label: 'Unscheduled' },
  { key: 'partial', label: 'Partial' },
  { key: 'scheduled', label: 'Scheduled' },
  { key: 'overbudget', label: 'Over Budget' },
  { key: 'overscheduled', label: 'Over-Scheduled' },
  { key: 'nobudget', label: 'No Budget' },
];
const DEPT_FILTERS = [{ id: 'ALL', label: 'All Depts' }, ...DEPT_GROUPS];
const PLACEHOLDER_LEVELS = [
  'Intern', 'Consultant', 'Staff A', 'Staff B', 'Senior Accountant',
  'Supervisor', 'Manager', 'Senior Manager', 'Principal', 'Member',
];
const RF_VALID_LEVELS = new Set(PLACEHOLDER_LEVELS);

export default function JobsView({
  scope = 'mine', enrichedJobs, myJobs, wipByJob, hrsByJob, staff, deps,
  meStaffId, isSuperUser, email, username,
  selectedJobId, setSelectedJobId, refreshSchedule, refreshDeps, patchJob, openChooser,
  closedJobs, clearClosedJobFuture, getDayCap, dayHoursByStaff, getContract,
}) {
  const navigate = useNavigate();
  const { refreshNotifications, poolAccess } = useOutletContext() || {};

  // ── UI state ──
  const [statusFilter, setStatusFilter] = useState('all');
  const [deptFilter, setDeptFilter] = useState('ALL');
  const [search, setSearch] = useState('');
  const [iddFrom, setIddFrom] = useState('');
  const [iddTo, setIddTo] = useState('');
  const [hideLowBudget, setHideLowBudget] = useState(false);
  const [sortCol, setSortCol] = useState('deadline');
  const [sortDir, setSortDir] = useState('asc');
  const [hideReviewStage, setHideReviewStage] = useState(true);
  const [hiddenStatuses, setHiddenStatuses] = useState(() => new Set());
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);
  const statusMenuRef = useRef(null);
  const [linkingJob, setLinkingJob] = useState(false);
  const [linkTarget, setLinkTarget] = useState('');
  const [linkSearch, setLinkSearch] = useState('');

  // ── Send to Pool (Job Pools, Trello #257) ──
  // One modal instance for both entry points (row button + sidebar action) so
  // there's a single confirm gate. `poolJob` holds the job being pooled.
  const [poolJob, setPoolJob] = useState(null);
  // suggestMaxHours drives the "this looks like a pool candidate" promotion on the
  // row button. Config-driven (rprs.PoolConfig suggest_max_hours, default 10) because the
  // threshold is expected to move. Falls back silently — a failed config fetch
  // should never break the jobs table.
  const [suggestMaxHours, setSuggestMaxHours] = useState(10);
  // Which services may be pooled at all. Server-enforced too; this just avoids
  // offering a button the server would reject. Null until the config resolves, at
  // which point isPoolableService falls back to its own defaults.
  const [poolableServices, setPoolableServices] = useState(null);
  useEffect(() => {
    let alive = true;
    fetchPoolConfig()
      .then(r => {
        if (!alive) return;
        if (r?.suggestMaxHours) setSuggestMaxHours(r.suggestMaxHours);
        if (Array.isArray(r?.poolableServices)) setPoolableServices(r.poolableServices);
      })
      .catch(() => { /* keep the defaults */ });
    return () => { alive = false; };
  }, []);

  // A pooled job is out of the manager's hands, so it offers no pool action —
  // just the pool it's waiting in. Audit/Attest work offers none either: those
  // engagements are team-staffed, so a preparer pool doesn't apply (item 1.4).
  // poolAccess first: while Job Pools is in pilot, everyone outside the group gets no
  // Send to Pool button anywhere. Server-enforced too — this stops us offering a
  // control that would 403.
  const canPool = useCallback(
    (j) => poolAccess
      && !j.PoolJobId
      && isPoolableService(j, poolableServices)
      && (isSuperUser || j.ManagerId === meStaffId || j.PartnerId === meStaffId),
    [poolAccess, isSuperUser, meStaffId, poolableServices],
  );

  const mgrSidebarRef = useRef(null);
  const closeMgrSidebar = useCallback(() => {
    if (mgrSidebarRef.current) mgrSidebarRef.current.classList.add('sched-mgr-sidebar--closing');
    const expandRow = document.querySelector('.sched-mgr-row--expanded-wip');
    if (expandRow) { expandRow.classList.remove('sched-expand-enter'); expandRow.classList.add('sched-expand-exit'); }
    setTimeout(() => setSelectedJobId(null), 150);
  }, [setSelectedJobId]);

  // ── Roll forward state ──
  const [rfOpen, setRfOpen] = useState(false);
  const [rfLoading, setRfLoading] = useState(false);
  const [rfSaving, setRfSaving] = useState(false);
  const [rfStep, setRfStep] = useState(1);
  const [rfPreview, setRfPreview] = useState(null);
  const [rfSelected, setRfSelected] = useState({});
  const [rfWeek, setRfWeek] = useState(null);
  const [rfWeekHint, setRfWeekHint] = useState(null);

  const computeRfSelections = useCallback((team, prevSelected = {}) => {
    const firstDate = team.reduce((min, m) => !m.firstDate ? min : (!min || m.firstDate < min ? m.firstDate : min), null);
    const updated = {};
    for (const m of team) {
      const key = m.staffId || m.peStaffIndex;
      const prev = prevSelected[key] || {};
      let offsetWeeks = 0;
      if (firstDate && m.firstDate) offsetWeeks = Math.round((new Date(m.firstDate) - new Date(firstDate)) / (7 * 86400000));
      updated[key] = {
        ...prev,
        checked: prev.checked !== undefined ? prev.checked : (!!m.staffId && m.pyHours > 3),
        hours: prev.hours !== undefined ? prev.hours : Math.round((m.pyHours || 0) * 4) / 4,
        weeks: prev.weeks !== undefined ? prev.weeks : Math.max(1, m.weekCount || 1),
        offsetWeeks: prev.offsetWeeks !== undefined ? prev.offsetWeeks : offsetWeeks,
        phLevel: prev.phLevel !== undefined ? prev.phLevel : '',
        roles: m.roles || [],
      };
    }
    return updated;
  }, []);

  // ── Roll Forward capacity plan ──
  // Each staffer's hours are spread evenly over the days in THEIR window that have free
  // capacity (spreadWithinCapacity): never onto a holiday, PTO, a part-time day off or a
  // full day. What doesn't fit is reported per row, never overbooked or dropped silently.
  // The Dashboard's schedule has no date bound, so a Jan–Jun plan sees every booking.
  // Days before today are skipped — a start week of "this week" mustn't book Monday
  // after Monday has passed.
  const rfWindowDays = useCallback((sel, weeks) => {
    if (!rfWeek) return [];
    const today = toISO(new Date());
    const start = addDaysLocal(rfWeek, (sel.offsetWeeks || 0) * 7);
    const out = [];
    for (let i = 0; i < Math.max(1, weeks || 1) * 7; i++) {
      const iso = toISO(addDaysLocal(start, i));
      if (iso >= today) out.push(iso);
    }
    return out;
  }, [rfWeek]);

  const rfPlan = useMemo(() => {
    const plan = {};
    if (!rfOpen || !rfPreview?.team || !getDayCap) return plan;
    const place = (staffId, days, hours) => spreadWithinCapacity({
      staffId, candidateDates: days, totalHours: hours, getDayCapFn: getDayCap, dayHoursMap: dayHoursByStaff,
      minDayHours: MIN_PLANNED_DAY_HOURS,
    });
    for (const m of rfPreview.team) {
      if (!m.staffId || m.isActive === false) continue; // placeholders have no capacity
      const key = m.staffId || m.peStaffIndex;
      const sel = rfSelected[key];
      if (!sel) continue;
      const hours = sel.checked ? Math.round((sel.hours || 0) * 4) / 4 : 0;
      const res = place(m.staffId, rfWindowDays(sel, sel.weeks), hours);
      // Shortest window that fits everything — the one-click "Extend to N wks".
      let fitWeeks = null;
      if (res.unassigned > 0) {
        for (let w = (sel.weeks || 1) + 1; w <= 52 && fitWeeks == null; w++) {
          if (place(m.staffId, rfWindowDays(sel, w), hours).unassigned <= 0) fitWeeks = w;
        }
      }
      plan[key] = { ...res, fitWeeks };
    }
    return plan;
  }, [rfOpen, rfPreview, rfSelected, rfWindowDays, getDayCap, dayHoursByStaff]);

  // ── Guided-tour drive ──
  const tourDrive = useTourDrive();
  const tourDriveHandledRef = useRef(null);
  useEffect(() => {
    if (tourDrive === tourDriveHandledRef.current) return;
    tourDriveHandledRef.current = tourDrive;
    const monday = new Date();
    monday.setHours(0, 0, 0, 0);
    monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));
    switch (tourDrive) {
      case 'mgr-select-job':
        setSelectedJobId(SENTINEL_JOB_ID);
        setTimeout(() => { document.querySelector(`[data-job-id="${SENTINEL_JOB_ID}"]`)?.scrollIntoView({ block: 'center', behavior: 'smooth' }); }, 150);
        break;
      case 'mgr-rollforward':
        setSelectedJobId(SENTINEL_JOB_ID);
        setRfOpen(true); setRfLoading(false); setRfStep(1);
        setRfPreview(TOUR_RF_PREVIEW);
        setRfSelected(computeRfSelections(TOUR_RF_PREVIEW.team));
        setRfWeek(monday);
        setRfWeekHint(null);
        break;
      case 'mgr-rollforward-preview':
        setRfStep(2);
        break;
      case null:
      case undefined:
        setRfOpen(false);
        setSelectedJobId(null);
        break;
      default:
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourDrive]);

  const handleSetOnSite = async (jobId, next) => {
    patchJob(jobId, { IsOnSite: next ? 1 : 0 });
    try { await setJobOnSite({ jobId, isOnSite: next }); }
    catch { patchJob(jobId, { IsOnSite: next ? 0 : 1 }); }
  };

  const isLateStage = useCallback((job) => isPostReview(job.StatusDetail), []);

  // ── Filtered jobs ──
  const filtered = useMemo(() => {
    let list = enrichedJobs;
    if (scope === 'mine') {
      if (!meStaffId) return [];
      list = list.filter(j => j.ManagerId === meStaffId || j.PartnerId === meStaffId);
      // Pooled jobs leave My Jobs (Job Pools, Trello #257). This decluttering is a
      // primary goal of the feature, not a side effect: small jobs that don't need
      // scheduling stop competing for the manager's attention. They live on the
      // dashboard Pool tab instead.
      //
      // All Jobs (scope='all', super-only) deliberately KEEPS them and shows a
      // pool chip — that view is the full picture, not an action queue.
      list = list.filter(j => !j.PoolJobId);
    }
    if (hideReviewStage) list = list.filter(j => !isLateStage(j));
    if (statusFilter !== 'all') list = list.filter(j => j.schedStatus === statusFilter);
    if (deptFilter !== 'ALL') list = list.filter(j => j.dept === deptFilter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(j =>
        (j.ClientName || '').toLowerCase().includes(q) ||
        (j.Name || '').toLowerCase().includes(q) ||
        (j.Code || '').toLowerCase().includes(q));
    }
    if (hiddenStatuses.size) list = list.filter(j => !hiddenStatuses.has(j.StatusDetail || '(No status)'));
    if (iddFrom) list = list.filter(j => j.Deadline && j.Deadline.slice(0, 10) >= iddFrom);
    if (iddTo) list = list.filter(j => j.Deadline && j.Deadline.slice(0, 10) <= iddTo);
    if (hideLowBudget) list = list.filter(j => (j.BudgetHours || 0) > 15);
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      let va, vb;
      switch (sortCol) {
        case 'client': va = (a.ClientName || '').toLowerCase(); vb = (b.ClientName || '').toLowerCase(); return va < vb ? -dir : va > vb ? dir : 0;
        case 'job': va = (a.Name || '').toLowerCase(); vb = (b.Name || '').toLowerCase(); return va < vb ? -dir : va > vb ? dir : 0;
        case 'type': va = (a.ServiceDesc || '').toLowerCase(); vb = (b.ServiceDesc || '').toLowerCase(); return va < vb ? -dir : va > vb ? dir : 0;
        case 'budget': return ((a.BudgetHours || 0) - (b.BudgetHours || 0)) * dir;
        case 'sched': return ((a.scheduledHrs || 0) - (b.scheduledHrs || 0)) * dir;
        case 'status': return (a.schedStatus || '').localeCompare(b.schedStatus || '') * dir;
        case 'progress': {
          const pctA = (a.BudgetHours || 0) > 0 ? (((wipByJob[a.JobId]?.total || 0) + (a.scheduledHrs || 0)) / a.BudgetHours) : 0;
          const pctB = (b.BudgetHours || 0) > 0 ? (((wipByJob[b.JobId]?.total || 0) + (b.scheduledHrs || 0)) / b.BudgetHours) : 0;
          return (pctA - pctB) * dir;
        }
        case 'deadline':
        default: { const da = a.Deadline || '9999'; const db = b.Deadline || '9999'; return da < db ? -dir : da > db ? dir : 0; }
      }
    });
  }, [enrichedJobs, scope, meStaffId, statusFilter, deptFilter, search, sortCol, sortDir, hideReviewStage, isLateStage, hiddenStatuses, wipByJob, iddFrom, iddTo, hideLowBudget]);

  const statusOptions = useMemo(() => {
    const present = new Set();
    for (const j of filtered) present.add(j.StatusDetail || '(No status)');
    for (const s of hiddenStatuses) present.add(s);
    const byPhase = new Map();
    for (const s of present) {
      const ph = s === '(No status)' ? -1 : getJobPhase(s);
      if (!byPhase.has(ph)) byPhase.set(ph, []);
      byPhase.get(ph).push(s);
    }
    return [0, 1, 2, 3, 4, -1].filter(p => byPhase.has(p)).map(p => ({
      phase: p, label: p === -1 ? 'Other / Unknown' : PHASE_LABELS[p],
      statuses: byPhase.get(p).sort((a, b) => a.localeCompare(b)),
    }));
  }, [filtered, hiddenStatuses]);

  useEffect(() => {
    if (!statusMenuOpen) return undefined;
    const handler = (e) => { if (statusMenuRef.current && !statusMenuRef.current.contains(e.target)) setStatusMenuOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [statusMenuOpen]);

  const stats = useMemo(() => computeJobStats(scope === 'all' ? enrichedJobs : myJobs), [scope, enrichedJobs, myJobs]);

  const selectedJob = useMemo(() => enrichedJobs.find(j => j.JobId === selectedJobId) || null, [enrichedJobs, selectedJobId]);
  const selectedEntries = useMemo(() => (selectedJobId && hrsByJob[selectedJobId]?.entries) || [], [selectedJobId, hrsByJob]);
  const groupedAssignments = useMemo(() => {
    if (!selectedEntries.length) return [];
    const byStaff = {};
    for (const e of selectedEntries) {
      const key = e.StaffId || e.StaffName || 'unknown';
      if (!byStaff[key]) byStaff[key] = { name: e.StaffName || 'Staff', totalHours: 0, minDate: e.EntryDate, maxDate: e.EntryDate, entries: 0 };
      byStaff[key].totalHours += e.Hours || 0;
      byStaff[key].entries++;
      if (e.EntryDate < byStaff[key].minDate) byStaff[key].minDate = e.EntryDate;
      if (e.EntryDate > byStaff[key].maxDate) byStaff[key].maxDate = e.EntryDate;
    }
    return Object.values(byStaff).sort((a, b) => a.name.localeCompare(b.name));
  }, [selectedEntries]);
  const selectedDeps = useMemo(
    () => selectedJobId ? deps.filter(d => d.PredecessorJobId === selectedJobId || d.DependentJobId === selectedJobId) : [],
    [selectedJobId, deps],
  );

  const handleSort = useCallback((col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir(col === 'budget' || col === 'sched' || col === 'progress' ? 'desc' : 'asc'); }
  }, [sortCol]);

  const handleRowClick = useCallback((jobId) => {
    if (selectedJobId === jobId) {
      closeMgrSidebar();
      const expandRow = document.querySelector('.sched-mgr-row--expanded-wip');
      if (expandRow) { expandRow.classList.remove('sched-expand-enter'); expandRow.classList.add('sched-expand-exit'); }
    } else {
      const expandRow = document.querySelector('.sched-mgr-row--expanded-wip');
      if (expandRow) {
        expandRow.classList.remove('sched-expand-enter'); expandRow.classList.add('sched-expand-exit');
        setTimeout(() => setSelectedJobId(jobId), 120);
      } else setSelectedJobId(jobId);
    }
  }, [selectedJobId, closeMgrSidebar, setSelectedJobId]);

  return (
    <div className="sched-mgr">
      {/* No onNavigateJob: the detail sidebar resolves jobs out of enrichedJobs, which
          excludes closed ones, so a click would only open an empty panel. */}
      {clearClosedJobFuture && (
        <ClosedJobsSection
          closedJobs={closedJobs} clearClosedJobFuture={clearClosedJobFuture}
          mode="manager" meStaffId={meStaffId} />
      )}

      <SchedStatBar items={[
        { value: stats.total, label: 'Total Jobs', color: BRAND.deepTeal },
        { value: stats.full, label: 'Fully Scheduled', color: BRAND.brightGreen },
        { value: stats.needs, label: 'Needs Scheduling', color: '#F59E0B' },
        { value: stats.overbudget, label: 'Over Budget', color: '#DC2626' },
        { value: stats.nobudget, label: 'No Budget', color: '#6B7280' },
      ]} />

      {/* ── Filter Bar ── */}
      <div className="sched-mgr-filters" style={scope === 'mine' ? { flexWrap: 'wrap' } : undefined}>
        {scope === 'mine' && (
          <div style={{ fontSize: 11, color: '#9CA3AF', width: '100%' }}>
            Jobs where you are <strong style={{ color: '#6B7280' }}>Job Partner</strong> or <strong style={{ color: '#6B7280' }}>Job Manager</strong>
            {' · '}
            <span className="sched-grid-detail-link" onClick={() => navigate('/schedule/reporting?tab=lookahead')}
              title="Who is scheduled on your jobs, week by week, with flags for time that may not belong there.">
              4-week lookahead &rarr;
            </span>
          </div>
        )}
        <div className="sched-mgr-filter-pills">
          {STATUS_FILTERS.map(sf => (
            <button key={sf.key}
              className={`sched-mgr-pill ${statusFilter === sf.key ? 'sched-mgr-pill--active' : ''}`}
              onClick={() => setStatusFilter(sf.key)}>
              {sf.key !== 'all' && <span className="sched-mgr-pill-dot" style={{ background: SCHED_STATUS[sf.key]?.dot || '#9CA3AF' }} />}
              {sf.label}
            </button>
          ))}
        </div>

        <select className="sched-mgr-dept-select" value={deptFilter} onChange={e => setDeptFilter(e.target.value)}>
          {DEPT_FILTERS.map(d => <option key={d.id} value={d.id}>{d.label}</option>)}
        </select>

        <div className="sched-mgr-filters-right">
          <div className="sched-mgr-due-filter" title="Filter by Internal Due Date">
            <span className="sched-mgr-filter-label">Due</span>
            <input type="date" className="sched-mgr-colfilter-date" value={iddFrom} max={iddTo || undefined}
              onChange={e => setIddFrom(e.target.value)} title="Internal Due Date on/after (from)" aria-label="Due date from" />
            <span className="sched-mgr-filter-label">–</span>
            <input type="date" className="sched-mgr-colfilter-date" value={iddTo} min={iddFrom || undefined}
              onChange={e => setIddTo(e.target.value)} title="Internal Due Date on/before (to)" aria-label="Due date to" />
          </div>

          <label className="sched-mgr-toggle" title="Hide jobs with 15h or less total budget (includes no budget)">
            <input type="checkbox" checked={hideLowBudget} onChange={e => setHideLowBudget(e.target.checked)} />
            <span className="sched-mgr-toggle-label">{'≤15h budget'}</span>
          </label>

          <label className="sched-mgr-toggle" title="Hide jobs at or past Member Review (phase 3) — service-agnostic; conservative on unmapped statuses">
            <input type="checkbox" checked={hideReviewStage} onChange={e => setHideReviewStage(e.target.checked)} />
            <span className="sched-mgr-toggle-label">Hide review-stage</span>
          </label>

          <div className="sched-mgr-statusmenu" ref={statusMenuRef}>
            <button type="button" className="sched-mgr-statusmenu-btn"
              onClick={() => setStatusMenuOpen(o => !o)} title="Show or hide jobs by their PE work status">
              Statuses{hiddenStatuses.size ? ` · ${hiddenStatuses.size} hidden` : ''} {'▾'}
            </button>
            {statusMenuOpen && (
              <div className="sched-mgr-statusmenu-panel">
                <div className="sched-mgr-statusmenu-actions">
                  <span className="sched-mgr-statusmenu-title">Show statuses</span>
                  <button type="button" onClick={() => setHiddenStatuses(new Set())} disabled={!hiddenStatuses.size}>Show all</button>
                </div>
                {statusOptions.length === 0 ? (
                  <div className="sched-mgr-statusmenu-empty">No jobs to filter.</div>
                ) : statusOptions.map(group => {
                  const allHidden = group.statuses.every(s => hiddenStatuses.has(s));
                  return (
                    <div key={group.phase} className="sched-mgr-statusmenu-group">
                      <label className="sched-mgr-statusmenu-grouphdr">
                        <input type="checkbox" checked={!allHidden}
                          onChange={() => setHiddenStatuses(prev => {
                            const next = new Set(prev);
                            if (allHidden) group.statuses.forEach(s => next.delete(s));
                            else group.statuses.forEach(s => next.add(s));
                            return next;
                          })} />
                        {group.label}
                      </label>
                      {group.statuses.map(s => (
                        <label key={s} className="sched-mgr-statusmenu-item">
                          <input type="checkbox" checked={!hiddenStatuses.has(s)}
                            onChange={() => setHiddenStatuses(prev => {
                              const next = new Set(prev);
                              if (next.has(s)) next.delete(s); else next.add(s);
                              return next;
                            })} />
                          {s}
                        </label>
                      ))}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <input className="sched-mgr-search" type="text" placeholder="Search client or job..."
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>
      </div>

      {/* ── Main Area (table + sidebar) ── */}
      <div className="sched-mgr-body">
        <div className="sched-mgr-table-wrap">
          <table className="sched-mgr-table">
            <thead>
              <tr>
                {[
                  { key: 'client', label: 'Client' },
                  { key: 'job', label: 'Job Name' },
                  { key: 'type', label: 'Type' },
                  { key: 'deadline', label: 'Internal Due Date' },
                  { key: 'budget', label: 'Budget Hrs', right: true },
                  { key: 'sched', label: 'Sched Hrs', right: true },
                  { key: 'status', label: 'Status' },
                ].map(col => {
                  const active = sortCol === col.key;
                  return (
                    <th key={col.key}
                      className={`sched-mgr-th-sortable${col.right ? ' sched-mgr-th-right' : ''}${active ? ' sched-mgr-th-sorted' : ''}`}
                      onClick={() => handleSort(col.key)}>
                      {col.label}
                      <span className={`sched-mgr-sort-arrows${active ? ' sched-mgr-sort-arrows--active' : ''}`}>
                        <span className={active && sortDir === 'asc' ? 'sched-mgr-sort-arrow--current' : ''}>{'▲'}</span>
                        <span className={active && sortDir === 'desc' ? 'sched-mgr-sort-arrow--current' : ''}>{'▼'}</span>
                      </span>
                    </th>
                  );
                })}
                <th style={{ width: 100 }}
                  className={`sched-mgr-th-sortable${sortCol === 'progress' ? ' sched-mgr-th-sorted' : ''}`}
                  onClick={() => handleSort('progress')}>
                  Progress
                  <span className={`sched-mgr-sort-arrows${sortCol === 'progress' ? ' sched-mgr-sort-arrows--active' : ''}`}>
                    <span className={sortCol === 'progress' && sortDir === 'asc' ? 'sched-mgr-sort-arrow--current' : ''}>{'▲'}</span>
                    <span className={sortCol === 'progress' && sortDir === 'desc' ? 'sched-mgr-sort-arrow--current' : ''}>{'▼'}</span>
                  </span>
                </th>
                {/* Pool column — far right so the row's own content stays primary. */}
                <th style={{ width: 96 }}>Pool</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={9} className="sched-mgr-empty">
                    {scope === 'mine' && !meStaffId
                      ? `We couldn't match your sign-in (${email || username || 'unknown'}) to a staff record. Refresh the page — this usually resolves on retry. If it persists, contact dataanalyticsteam@bmss.com.`
                      : 'No jobs match the current filters.'}
                  </td>
                </tr>
              ) : (
                filtered.map(j => {
                  const st = SCHED_STATUS[j.schedStatus] || SCHED_STATUS.unscheduled;
                  const typeColor = JOB_TYPE_COLORS[j.ServiceDesc] || JOB_TYPE_COLORS[j.JobType] || { bg: '#E5E7EB', text: '#374151' };
                  const isSelected = j.JobId === selectedJobId;
                  const wip = wipByJob[j.JobId];
                  const budget = j.BudgetHours || 0;
                  const wipTotal = wip?.total || 0;
                  const schedTotal = j.scheduledHrs || 0;
                  const totalUsed = wipTotal + schedTotal;
                  const progressPct = budget > 0 ? Math.round((totalUsed / budget) * 100) : 0;
                  const isOverBudget = totalUsed > budget;
                  const wipInBudget = Math.min(wipTotal, budget);
                  const wipOver = Math.max(0, wipTotal - budget);
                  const schedInBudget = Math.min(schedTotal, Math.max(0, budget - wipTotal));
                  const schedOver = Math.max(0, totalUsed - Math.max(budget, wipTotal));
                  const barTotal = Math.max(totalUsed, budget);
                  const wipInPct = barTotal > 0 ? Math.round((wipInBudget / barTotal) * 100) : 0;
                  const schedInPct = barTotal > 0 ? Math.round((schedInBudget / barTotal) * 100) : 0;
                  const wipOverPct = barTotal > 0 ? Math.round((wipOver / barTotal) * 100) : 0;
                  const schedOverPct = barTotal > 0 ? Math.round((schedOver / barTotal) * 100) : 0;

                  return (
                    <React.Fragment key={j.JobId}>
                      <tr data-job-id={j.JobId}
                        className={`sched-mgr-row ${isSelected ? 'sched-mgr-row--selected' : ''}`}
                        onClick={() => handleRowClick(j.JobId)}>
                        <td className="sched-mgr-cell-client">
                          <span className="sched-mgr-client-code">{j.ClientCode || j.Code}</span>
                          <span className="sched-mgr-client-name sched-mgr-client-link"
                            onClick={(e) => { e.stopPropagation(); navigate(`/schedule/client/${j.ClientId}`); }}>{j.ClientName}</span>
                          {j.ClientStatus === 'FINANCIAL' && <span className="sched-client-status-badge sched-client-status-badge--financial" title="Client status: FINANCIAL (Stop Work)">⚠ Financial</span>}
                          {j.ClientStatus === 'LOST' && <span className="sched-client-status-badge sched-client-status-badge--lost" title="Client status: LOST">✕ Lost</span>}
                        </td>
                        <td className="sched-mgr-cell-jobname">
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                            <span>{j.Name}</span>
                            {(j.IsOnSite || isSuperUser || j.ManagerId === meStaffId || j.PartnerId === meStaffId) && (
                              <span onClick={(e) => e.stopPropagation()}>
                                <OnSiteToggle isOnSite={!!j.IsOnSite} office={j.Office} officeCode={j.OfficeCode}
                                  canEdit={isSuperUser || j.ManagerId === meStaffId || j.PartnerId === meStaffId}
                                  onChange={(next) => handleSetOnSite(j.JobId, next)} />
                              </span>
                            )}
                          </div>
                        </td>
                        <td><span className="sched-type-tag" style={{ background: typeColor.bg, color: typeColor.text }}>{j.ServiceDesc || j.JobType || 'General'}</span></td>
                        <td>{j.Deadline ? fmtDate(j.Deadline) : '—'}</td>
                        <td className="sched-mgr-td-right">{j.BudgetHours || 0}</td>
                        <td className="sched-mgr-td-right">{Math.round(j.scheduledHrs * 100) / 100}</td>
                        <td><span className="sched-badge" style={{ background: st.bg, color: st.text }}><span className="sched-mgr-status-dot" style={{ background: st.dot }} />{st.label}</span></td>
                        <td>
                          {budget > 0 && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                              <div style={{ width: 80, height: 6, background: '#E5E7EB', borderRadius: 3, display: 'flex', overflow: 'hidden' }}>
                                {wipInPct > 0 && <div style={{ width: `${wipInPct}%`, background: BRAND.oceanBlue }} />}
                                {schedInPct > 0 && <div style={{ width: `${schedInPct}%`, background: BRAND.sageGreen }} />}
                                {wipOverPct > 0 && <div style={{ width: `${wipOverPct}%`, background: '#DC2626' }} />}
                                {schedOverPct > 0 && <div style={{ width: `${schedOverPct}%`, background: '#EA580C' }} />}
                              </div>
                              <span style={{ fontSize: 10, color: isOverBudget ? '#DC2626' : '#6B7280', fontWeight: isOverBudget ? 700 : 400, minWidth: 28 }}>{progressPct}%</span>
                            </div>
                          )}
                        </td>
                        {/* Pool: send it, or show where it already is. Promoted styling
                            when the job is small enough to look like a pool candidate. */}
                        <td onClick={(e) => e.stopPropagation()}>
                          {j.PoolJobId ? (
                            <span className="sched-pool-row-state" title={poolChipTitle(j)}>
                              <span
                                className="sched-pool-chip"
                                style={{
                                  background: POOL_META[j.PoolCode]?.color || '#6B7280',
                                  // Claimed episodes are history, not an open offer —
                                  // mute them so the eye goes to jobs still waiting.
                                  opacity: j.PoolState === 'claimed' ? 0.55 : 1,
                                }}
                              >
                                {POOL_META[j.PoolCode]?.label || j.PoolCode}
                                {j.PoolState === 'claimed' ? ' · taken' : ''}
                              </span>
                            </span>
                          ) : canPool(j) ? (
                            <button
                              type="button"
                              className={`sched-pool-row-btn${budget > 0 && budget < suggestMaxHours ? ' sched-pool-row-btn--suggest' : ''}`}
                              onClick={() => setPoolJob(j)}
                              title={budget > 0 && budget < suggestMaxHours
                                ? `Only ${budget}h budgeted — a good candidate for a preparer pool`
                                : 'Release this job into a preparer pool'}
                            >
                              Send to Pool
                            </button>
                          ) : null}
                        </td>
                      </tr>
                      {isSelected && wip && budget > 0 && (
                        <tr className="sched-mgr-row--expanded-wip sched-expand-enter">
                          <td colSpan={9} style={{ padding: '10px 16px', background: '#F9FAFB', borderBottom: '2px solid #E5E7EB' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                              <span style={{ fontSize: 12, fontWeight: 700, color: BRAND.deepTeal }}>
                                {Math.round(wipTotal * 100) / 100}h logged + {Math.round(schedTotal * 100) / 100}h scheduled
                              </span>
                              <span style={{ fontSize: 12, color: '#6B7280' }}>of</span>
                              <span style={{ fontSize: 12, fontWeight: 700, color: '#374151' }}>{budget}h budget</span>
                              {isOverBudget ? (
                                <span style={{ display: 'flex', gap: 8, marginLeft: 4 }}>
                                  {wipOver > 0 && <span style={{ fontSize: 12, fontWeight: 700, color: '#DC2626' }}>+{Math.round(wipOver * 100) / 100}h over budget</span>}
                                  {schedOver > 0 && <span style={{ fontSize: 12, fontWeight: 700, color: '#EA580C' }}>+{Math.round(schedOver * 100) / 100}h over-scheduled</span>}
                                </span>
                              ) : (
                                <span style={{ fontSize: 12, fontWeight: 700, color: '#F59E0B', marginLeft: 4 }}>{Math.round((budget - wipTotal - schedTotal) * 100) / 100}h remaining</span>
                              )}
                            </div>
                            <div style={{ position: 'relative', marginBottom: 6, marginTop: 8 }}>
                              <div style={{ display: 'flex', height: 10, background: '#E5E7EB', borderRadius: 5, overflow: 'hidden' }}>
                                {wipInPct > 0 && <div style={{ width: `${wipInPct}%`, background: BRAND.oceanBlue }} />}
                                {schedInPct > 0 && <div style={{ width: `${schedInPct}%`, background: BRAND.sageGreen }} />}
                                {wipOverPct > 0 && <div style={{ width: `${wipOverPct}%`, background: '#DC2626' }} />}
                                {schedOverPct > 0 && <div style={{ width: `${schedOverPct}%`, background: '#EA580C' }} />}
                              </div>
                              {isOverBudget && (
                                <div style={{ position: 'absolute', top: -2, height: 18, left: `${Math.round((budget / barTotal) * 100)}%`, borderLeft: '2px dashed #374151' }}>
                                  <span style={{ position: 'absolute', bottom: -14, left: -14, fontSize: 8, color: '#374151', fontWeight: 700, whiteSpace: 'nowrap' }}>Budget</span>
                                </div>
                              )}
                            </div>
                            <div style={{ display: 'flex', gap: 12, fontSize: 10, color: '#9CA3AF', marginBottom: 8, flexWrap: 'wrap' }}>
                              <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><span style={{ width: 8, height: 8, borderRadius: 2, background: BRAND.oceanBlue }} /> WIP Logged</span>
                              <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><span style={{ width: 8, height: 8, borderRadius: 2, background: BRAND.sageGreen }} /> Scheduled</span>
                              {!isOverBudget && <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><span style={{ width: 8, height: 8, borderRadius: 2, background: '#E5E7EB' }} /> Remaining</span>}
                              {wipOverPct > 0 && <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><span style={{ width: 8, height: 8, borderRadius: 2, background: '#DC2626' }} /> Over Budget (WIP)</span>}
                              {schedOverPct > 0 && <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><span style={{ width: 8, height: 8, borderRadius: 2, background: '#EA580C' }} /> Over-Scheduled</span>}
                            </div>
                            <div style={{ display: 'flex', gap: 24, fontSize: 11 }}>
                              {wip.byStaff.length > 0 && (
                                <div>
                                  <div style={{ fontWeight: 700, color: '#374151', marginBottom: 3 }}>WIP by Staff</div>
                                  {wip.byStaff.map((s, i) => (
                                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 20, color: '#6B7280', padding: '1px 0' }}>
                                      <span>{s.name}</span><span style={{ fontWeight: 600, color: '#374151' }}>{Math.round(s.hours * 100) / 100}h</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                              {((hrsByJob[j.JobId]?.entries || []).length > 0 || (j.placeholderHrs || 0) > 0) && (
                                <div>
                                  <div style={{ fontWeight: 700, color: '#374151', marginBottom: 3 }}>Scheduled by Staff</div>
                                  {/* Placeholder hours are inside schedTotal, so they have to be
                                      itemised here or the list silently sums short of the header
                                      (248h scheduled over three 80h staff). Listed first and
                                      styled apart — it is a (dept, level) slot, not a person. */}
                                  {(j.placeholderHrs || 0) > 0 && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 20, color: '#B45309', fontStyle: 'italic', padding: '1px 0' }}>
                                      <span>Placeholder</span><span style={{ fontWeight: 600 }}>{Math.round(j.placeholderHrs * 100) / 100}h</span>
                                    </div>
                                  )}
                                  {(() => {
                                    const staffMap = {};
                                    for (const e of (hrsByJob[j.JobId]?.entries || [])) {
                                      if (!staffMap[e.StaffId]) staffMap[e.StaffId] = { name: e.StaffName || 'Unknown', hours: 0 };
                                      staffMap[e.StaffId].hours += e.Hours || 0;
                                    }
                                    return Object.values(staffMap).sort((a, b) => b.hours - a.hours).map((s, i) => (
                                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 20, color: '#6B7280', padding: '1px 0' }}>
                                        <span>{s.name}</span><span style={{ fontWeight: 600, color: '#374151' }}>{Math.round(s.hours * 100) / 100}h</span>
                                      </div>
                                    ));
                                  })()}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* ── Detail Sidebar ── */}
        {selectedJob && (
          <aside ref={mgrSidebarRef} className="sched-mgr-sidebar">
            <div className="sched-mgr-sidebar-header">
              <h3 className="sched-mgr-sidebar-title">{selectedJob.Name}</h3>
              <button className="sched-mgr-sidebar-close" onClick={closeMgrSidebar} title="Close">&times;</button>
            </div>

            <div className="sched-mgr-sidebar-section">
              <div className="sched-mgr-sidebar-row">
                <span className="sched-mgr-sidebar-label">Client</span>
                <span className="sched-mgr-sidebar-value sched-mgr-client-link" onClick={() => navigate(`/schedule/client/${selectedJob.ClientId}`)}>{selectedJob.ClientName}</span>
              </div>
              {selectedJob.PEJobIndex && (
                <div className="sched-mgr-sidebar-row">
                  <span className="sched-mgr-sidebar-label">PE Job</span>
                  <a href={`https://bmss.pehosted.com/PE/Job/Job/${selectedJob.PEJobIndex}?panel=0`}
                    target="_blank" rel="noopener noreferrer"
                    style={{ fontSize: 12, fontWeight: 600, color: '#356371', textDecoration: 'none' }}
                    onClick={e => e.stopPropagation()}>Open in PE &#8599;</a>
                </div>
              )}
              <div className="sched-mgr-sidebar-row">
                <span className="sched-mgr-sidebar-label">Type</span>
                <span className="sched-type-tag" style={{
                  background: (JOB_TYPE_COLORS[selectedJob.ServiceDesc] || JOB_TYPE_COLORS[selectedJob.JobType] || {}).bg || '#E5E7EB',
                  color: (JOB_TYPE_COLORS[selectedJob.ServiceDesc] || JOB_TYPE_COLORS[selectedJob.JobType] || {}).text || '#374151',
                }}>{selectedJob.ServiceDesc || selectedJob.JobType || 'General'}</span>
              </div>
              <div className="sched-mgr-sidebar-row">
                <span className="sched-mgr-sidebar-label">Internal Due Date</span>
                <span className="sched-mgr-sidebar-value">{selectedJob.Deadline ? fmtDate(selectedJob.Deadline) : '—'}</span>
              </div>
              <div className="sched-mgr-sidebar-row">
                <span className="sched-mgr-sidebar-label">Work Status</span>
                <span className="sched-mgr-sidebar-value">{selectedJob.StatusDetail || selectedJob.Status || 'Not Started'}</span>
              </div>
            </div>

            <div className="sched-mgr-sidebar-section">
              <div className="sched-mgr-sidebar-row"><span className="sched-mgr-sidebar-label">Partner</span><span className="sched-mgr-sidebar-value">{selectedJob.PartnerName || '—'}</span></div>
              <div className="sched-mgr-sidebar-row"><span className="sched-mgr-sidebar-label">Manager</span><span className="sched-mgr-sidebar-value">{selectedJob.ManagerName || '—'}</span></div>
              {(selectedJob.NicheName || selectedJob.ClientIndustry) && (
                <div className="sched-mgr-sidebar-row"><span className="sched-mgr-sidebar-label">Niche</span><span className="sched-mgr-sidebar-value">{selectedJob.NicheName || selectedJob.ClientIndustry}</span></div>
              )}
              {selectedJob.ServiceDesc && (
                <div className="sched-mgr-sidebar-row"><span className="sched-mgr-sidebar-label">Service</span><span className="sched-mgr-sidebar-value">{selectedJob.ServiceDesc}</span></div>
              )}
            </div>

            {(groupedAssignments.length > 0 || (selectedJob.PlaceholderHours || 0) > 0) && (
              <div className="sched-mgr-sidebar-section">
                <div className="sched-mgr-sidebar-section-title">
                  Current Assignments ({groupedAssignments.length} staff{(selectedJob.PlaceholderHours || 0) > 0 ? ` + ${selectedJob.PlaceholderCount > 1 ? `${selectedJob.PlaceholderCount} placeholders` : 'placeholder'}` : ''})
                </div>
                <div className="sched-mgr-assign-list">
                  {/* Placeholder hours are part of this job's scheduled total, so the list
                      has to show them or it reads as if 8 of the 248h came from nowhere.
                      Amber + italic: it holds calendar space but has no staffer yet. */}
                  {(selectedJob.PlaceholderHours || 0) > 0 && (
                    <div className="sched-mgr-assign-item">
                      <span className="sched-mgr-assign-name" style={{ color: '#B45309', fontStyle: 'italic' }}>Placeholder</span>
                      <span className="sched-mgr-assign-detail">
                        {selectedJob.PlaceholderStart
                          ? `${fmtShortDate(selectedJob.PlaceholderStart)}${String(selectedJob.PlaceholderStart).slice(0, 10) !== String(selectedJob.PlaceholderEnd).slice(0, 10) ? ` – ${fmtShortDate(selectedJob.PlaceholderEnd)}` : ''} · ` : ''}
                        {Math.round(selectedJob.PlaceholderHours * 100) / 100}h
                      </span>
                    </div>
                  )}
                  {groupedAssignments.map((g, i) => (
                    <div key={i} className="sched-mgr-assign-item">
                      <span className="sched-mgr-assign-name">{g.name}</span>
                      <span className="sched-mgr-assign-detail">
                        {fmtShortDate(g.minDate)}{g.minDate !== g.maxDate ? ` – ${fmtShortDate(g.maxDate)}` : ''} &middot; {Math.round(g.totalHours * 100) / 100}h
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 16 }}>
              <button className="sched-assign-btn sched-mgr-sidebar-action" onClick={() => {
                const budget = selectedJob.BudgetHours || 0;
                const wip = wipByJob[selectedJob.JobId]?.total || 0;
                const sched = selectedJob.scheduledHrs || 0;
                openChooser(
                  { JobId: selectedJob.JobId, Name: selectedJob.Name, ClientName: selectedJob.ClientName,
                    remaining: Math.max(0, Math.round((budget - wip - sched) * 100) / 100), wipTotal: wip,
                    dept: jobDept(selectedJob.ServiceDesc), Deadline: selectedJob.Deadline || null },
                  { variant: 'myjobs', origin: 'myjobs' },
                );
              }}>Schedule Staff</button>
              {/* Send to Pool — the other way to get this job worked. Sits beside
                  Schedule Staff because they're the two dispositions of a job you
                  lead: schedule someone, or release it to a pool. */}
              {selectedJob.PoolJobId ? (
                <div className="sched-pool-sidebar-state">
                  <span
                    className="sched-pool-chip"
                    style={{
                      background: POOL_META[selectedJob.PoolCode]?.color || '#6B7280',
                      opacity: selectedJob.PoolState === 'claimed' ? 0.55 : 1,
                    }}
                  >
                    {POOL_META[selectedJob.PoolCode]?.label || selectedJob.PoolCode}
                  </span>
                  <span>
                    {selectedJob.PoolState === 'claimed'
                      ? 'Claimed out of the pool — tracking continues in Practice Engine'
                      : `Waiting in the pool since ${fmtShortDate(selectedJob.PooledAt)}`}
                  </span>
                </div>
              ) : canPool(selectedJob) ? (
                <button
                  className={`sched-pool-sidebar-btn sched-mgr-sidebar-action${(selectedJob.BudgetHours || 0) > 0 && (selectedJob.BudgetHours || 0) < suggestMaxHours ? ' sched-pool-sidebar-btn--suggest' : ''}`}
                  onClick={() => setPoolJob(selectedJob)}
                  title={(selectedJob.BudgetHours || 0) > 0 && (selectedJob.BudgetHours || 0) < suggestMaxHours
                    ? `Only ${selectedJob.BudgetHours}h budgeted — a good candidate for a preparer pool`
                    : 'Release this job into a preparer pool'}
                >
                  &#8594; Send to Pool
                </button>
              ) : null}
              {selectedJob.PEJobIndex_PY && (
                <button className="sched-assign-btn sched-mgr-sidebar-action"
                  style={{ background: '#FEF3C7', color: '#92400E', borderColor: '#F59E0B' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#FDE68A'}
                  onMouseLeave={e => e.currentTarget.style.background = '#FEF3C7'}
                  onClick={async () => {
                    setRfOpen(true); setRfLoading(true); setRfPreview(null); setRfStep(1);
                    try {
                      const result = await rollForwardAPI({ targetJobId: selectedJob.JobId });
                      setRfPreview(result);
                      setRfSelected(computeRfSelections(result.team || []));
                      const def = rfDefaultWeek(result.team);
                      setRfWeek(def.week);
                      setRfWeekHint(def.hint);
                    } catch (e) { alert('Roll forward failed: ' + e.message); setRfOpen(false); }
                    finally { setRfLoading(false); }
                  }}>&#8635; Roll Forward PY Team</button>
              )}
            </div>

            <div className="sched-mgr-sidebar-section">
              <div className="sched-mgr-sidebar-section-title">Dependencies ({selectedDeps.length})</div>
              {selectedDeps.map(d => (
                <div key={d.DependencyId} className="sched-cv-dep-card" style={{ marginBottom: 6 }}>
                  <span>&#128279;</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 600 }}>
                      {d.PredecessorJobId === selectedJob.JobId ? `${d.DependentJobName} depends on this` : `Depends on ${d.PredecessorJobName}`}
                    </div>
                    {d.Notes && <div style={{ fontSize: 11, color: '#6B7280' }}>{d.Notes}</div>}
                  </div>
                  <button style={{ background: 'none', border: 'none', color: '#EF4444', fontSize: 14, cursor: 'pointer', padding: 2 }}
                    title="Remove link"
                    onClick={async () => { await removeDependency(d.DependencyId); await refreshDeps(); }}>&#10005;</button>
                </div>
              ))}
              <button className="sched-mgr-pill" style={{ marginTop: 4, fontSize: 11 }}
                onClick={() => { setLinkingJob(true); setLinkTarget(''); setLinkSearch(''); }}>+ Link Job</button>
            </div>
          </aside>
        )}
      </div>

      {/* ── Roll Forward Modal (2-Step) ── */}
      {rfOpen && selectedJob && (() => {
        const checkedStaff = Object.entries(rfSelected).filter(([, s]) => s.checked && s.hours > 0);
        const totalHrs = checkedStaff.reduce((s, [, v]) => s + (v.hours || 0), 0);
        const rfIsUnassigned = (m) => !m.staffId || m.isActive === false;
        const rfNeedsPicker = (m) => rfIsUnassigned(m) && !RF_VALID_LEVELS.has(m.level);
        // Capacity outcome across the checked team: placeholders aren't people, so their
        // hours always "fit"; real staff contribute what rfPlan could place.
        const rfShort = [];
        let rfPlacedHrs = 0;
        for (const m of (rfPreview?.team || [])) {
          const key = m.staffId || m.peStaffIndex;
          const sel = rfSelected[key];
          if (!sel?.checked || !(sel.hours > 0)) continue;
          const p = rfPlan[key];
          if (!p) { rfPlacedHrs += sel.hours || 0; continue; }
          rfPlacedHrs += p.totalAssigned;
          if (p.unassigned > 0) rfShort.push({ key, name: m.name, unassigned: p.unassigned, fitWeeks: p.fitWeeks });
        }
        const rfShortHrs = roundQ(rfShort.reduce((s, r) => s + r.unassigned, 0));
        rfPlacedHrs = roundQ(rfPlacedHrs);
        const rfPhDept = (m) => { const n = normalizeDept(m.department); return (n && n !== 'OTHER' && n !== 'ADMIN') ? n : (selectedJob?.dept || 'OTHER'); };
        const rfDeptShort = (code) => (DEPT_GROUPS.find(g => g.id === code)?.label || code || '').split(' ')[0];
        const rfNeedsLevel = (rfPreview?.team || []).some(m => {
          if (!rfNeedsPicker(m)) return false;
          const sel = rfSelected[m.staffId || m.peStaffIndex];
          return sel?.checked && sel.hours > 0 && !sel.phLevel;
        });
        let maxEndWeek = 0;
        const ganttBars = (rfPreview?.team || []).filter(m => {
          const key = m.staffId || m.peStaffIndex;
          const sel = rfSelected[key];
          return sel?.checked && sel.hours > 0;
        }).map(m => {
          const key = m.staffId || m.peStaffIndex;
          const sel = rfSelected[key];
          const startWk = sel.offsetWeeks || 0;
          const endWk = startWk + (sel.weeks || 1);
          if (endWk > maxEndWeek) maxEndWeek = endWk;
          const unassigned = rfIsUnassigned(m);
          const level = rfNeedsPicker(m) ? (sel.phLevel || '') : m.level;
          return { key, name: m.name, level, roles: m.roles || [], unassigned, startWk, endWk, hours: sel.hours, weeks: sel.weeks || 1 };
        });
        let deadlineWk = null;
        const dueDate = selectedJob.Deadline;
        if (dueDate && rfWeek) deadlineWk = Math.round((new Date(dueDate) - rfWeek) / (7 * 86400000) * 10) / 10;
        const totalWks = Math.max(maxEndWeek + 1, (deadlineWk || 0) + 2, 4);
        const projectedFinishWk = maxEndWeek;
        const overDeadline = deadlineWk != null && projectedFinishWk > deadlineWk;

        return (
          <div className="sched-mgr-modal-backdrop" onClick={() => setRfOpen(false)}>
            <div className="sched-mgr-modal" style={{ width: rfStep === 2 ? 900 : 780, maxWidth: '92vw', maxHeight: '88vh' }} onClick={e => e.stopPropagation()}>
              <div style={{ background: BRAND.deepTeal, color: '#fff', margin: '-24px -24px 16px', padding: '16px 24px', borderRadius: '14px 14px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>Roll Forward: {selectedJob.ClientName}</div>
                  <div style={{ fontSize: 12, opacity: 0.75, marginTop: 2 }}>{selectedJob.Name}{dueDate && <> &middot; Due {fmtShortDate(dueDate)}</>}</div>
                </div>
                <button style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', borderRadius: 6, padding: '6px 10px', cursor: 'pointer', fontSize: 16 }} onClick={() => setRfOpen(false)}>&times;</button>
              </div>

              <div className="rf-steps">
                <div className={`rf-step${rfStep === 1 ? ' rf-step--active' : ''}`}><div className="rf-step-num">1</div> Configure</div>
                <span style={{ color: '#D1D5DB' }}>&rarr;</span>
                <div className={`rf-step${rfStep === 2 ? ' rf-step--active' : ''}`}><div className="rf-step-num">2</div> Preview & Confirm</div>
              </div>

              {rfLoading && <div style={{ textAlign: 'center', padding: 40, color: '#9CA3AF' }}>Loading prior year data...</div>}

              {!rfLoading && rfPreview && (
                <>
                  <div style={{ background: '#F9FAFB', borderRadius: 8, padding: 12, marginBottom: 14 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Prior Year &mdash; {rfPreview.pyJob?.Name || 'Unknown'}</div>
                    <div style={{ display: 'flex', gap: 16, fontSize: 12, color: '#374151', flexWrap: 'wrap' }}>
                      {rfPreview.pyJob?.BudgetHours > 0 && <span>Budget: <strong>{rfPreview.pyJob.BudgetHours}h</strong></span>}
                      {rfPreview.pyJob?.ActualHours > 0 && <span>Actual: <strong>{Math.round(rfPreview.pyJob.ActualHours * 100) / 100}h</strong></span>}
                      <span>Team: <strong>{rfPreview.team?.length || 0}</strong> staff</span>
                    </div>
                  </div>

                  {rfPreview.team?.length === 0 && (
                    <div style={{ textAlign: 'center', padding: 30, color: '#9CA3AF' }}>
                      <div style={{ fontSize: 24, marginBottom: 8 }}>&#128197;</div>
                      <div>No prior year team data found. Schedule staff manually.</div>
                    </div>
                  )}

                  {rfPreview.team?.length > 0 && rfStep === 1 && (
                    <>
                      <div className="sched-mgr-modal-field" style={{ marginBottom: 12 }}>
                        <label className="sched-mgr-modal-label">Job Start Week (Monday)</label>
                        <input type="date" value={rfWeek ? rfWeek.toISOString().split('T')[0] : ''}
                          onChange={e => {
                            const d = new Date(e.target.value + 'T00:00:00');
                            if (isNaN(d)) return;
                            const day = d.getDay();
                            const diff = day === 0 ? 1 : (day === 1 ? 0 : -(day - 1));
                            d.setDate(d.getDate() + diff);
                            setRfWeek(d);
                            setRfWeekHint(null);
                            if (rfPreview?.team?.length > 0) setRfSelected(computeRfSelections(rfPreview.team, rfSelected));
                          }}
                          style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #D1D5DB', fontSize: 13, width: 160 }} />
                        {rfWeekHint && <div style={{ fontSize: 11, color: '#6B7280', marginTop: 4 }}>{rfWeekHint}</div>}
                        <div style={{ fontSize: 11, color: '#6B7280', marginTop: 4 }}>
                          Hours are spread over each person&rsquo;s weeks around their contract hours, PTO, holidays and work already scheduled. Nobody is overbooked; anything that doesn&rsquo;t fit is flagged below.
                        </div>
                      </div>

                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                          <thead>
                            <tr style={{ background: '#F3F4F6' }}>
                              <th style={{ padding: '7px 6px', width: 28 }}></th>
                              <th style={{ padding: '7px 6px', textAlign: 'left' }}>Staff</th>
                              <th style={{ padding: '7px 6px', textAlign: 'left', fontSize: 11 }}>Role(s)</th>
                              <th style={{ padding: '7px 6px', textAlign: 'right', fontSize: 11 }}>PY Hrs</th>
                              <th style={{ padding: '7px 6px', textAlign: 'center', fontSize: 11 }}>PY Span</th>
                              <th style={{ padding: '7px 6px', textAlign: 'right' }}>CY Hrs</th>
                              <th style={{ padding: '7px 6px', textAlign: 'center' }}>Weeks</th>
                              <th style={{ padding: '7px 6px', textAlign: 'center', fontSize: 11 }}>Offset</th>
                              <th style={{ padding: '7px 6px', textAlign: 'right', fontSize: 11 }}>Hrs/Wk</th>
                              <th style={{ padding: '7px 6px', textAlign: 'right', fontSize: 11 }}
                                title="Free capacity in this person's weeks: contract hours minus PTO, holidays and work already scheduled">Room</th>
                            </tr>
                          </thead>
                          <tbody>
                            {rfPreview.team.map(m => {
                              const key = m.staffId || m.peStaffIndex;
                              const sel = rfSelected[key] || { checked: !!m.staffId && m.pyHours > 3, hours: Math.round((m.pyHours || 0) * 4) / 4, weeks: Math.max(1, m.weekCount || 1), offsetWeeks: 0, phLevel: '' };
                              const unassigned = rfIsUnassigned(m);
                              const needsPicker = rfNeedsPicker(m);
                              // Average only, shown in the 0.25h units hours are actually placed in
                              // ("~" when rounded) — the save uses the day-by-day capacity plan.
                              const rawPerWeek = sel.weeks > 0 ? sel.hours / sel.weeks : 0;
                              const hrsPerWeek = Math.round(rawPerWeek * 4) / 4;
                              const perWeekApprox = Math.abs(rawPerWeek - hrsPerWeek) > 0.001;
                              // Hrs/Wk is judged against THIS person's room, not a flat 40h:
                              // red = won't all fit, amber = uses most of what's free.
                              const p = rfPlan[key];
                              const tight = p && sel.checked && sel.hours > 0
                                ? (p.unassigned > 0 ? 'short' : sel.hours > 0.8 * p.freeHours ? 'tight' : null)
                                : null;
                              const startISO = rfWeek ? toISO(addDaysLocal(rfWeek, (sel.offsetWeeks || 0) * 7)) : undefined;
                              return (
                                <tr key={key} style={{ borderBottom: '1px solid #F3F4F6', background: unassigned ? '#F1F5F9' : '#fff' }}>
                                  <td style={{ padding: '6px' }}>
                                    <input type="checkbox" checked={!!sel.checked} onChange={e => setRfSelected(prev => ({ ...prev, [key]: { ...sel, checked: e.target.checked } }))} />
                                  </td>
                                  <td style={{ padding: '6px', whiteSpace: 'nowrap' }}>
                                    {unassigned ? (
                                      <>
                                        <div style={{ fontWeight: 600, color: '#003D4C', fontSize: 12 }}>Placeholder <span style={{ fontSize: 10, color: '#9CA3AF', fontWeight: 400 }}>&middot; {rfDeptShort(rfPhDept(m))}</span></div>
                                        {needsPicker ? (
                                          <select value={sel.phLevel || ''} disabled={!sel.checked}
                                            onChange={e => setRfSelected(prev => ({ ...prev, [key]: { ...sel, phLevel: e.target.value } }))}
                                            style={{ fontSize: 10, padding: '1px 3px', marginTop: 2, border: `1px solid ${sel.checked && !sel.phLevel ? '#DC2626' : '#E5E7EB'}`, borderRadius: 4 }}>
                                            <option value="">Select level...</option>
                                            {PLACEHOLDER_LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
                                          </select>
                                        ) : (<div style={{ fontSize: 10, color: '#9CA3AF' }}>{m.level}</div>)}
                                      </>
                                    ) : (
                                      <>
                                        <div style={{ fontWeight: 600, color: '#111827', fontSize: 12 }}>{m.name}</div>
                                        <div style={{ fontSize: 10, color: '#9CA3AF' }}>
                                          {m.level} <ContractChip contract={getContract?.(m.staffId, startISO)} />
                                        </div>
                                      </>
                                    )}
                                  </td>
                                  <td style={{ padding: '6px' }}>
                                    {(m.roles || []).slice(0, 2).map(r => <span key={r} className="rf-role-chip" style={{ background: ROLE_COLORS[r] || '#6B7280', color: '#fff' }}>{r}</span>)}
                                    {(m.roles || []).length > 2 && <span style={{ fontSize: 10, color: '#9CA3AF' }}>+{m.roles.length - 2}</span>}
                                  </td>
                                  <td style={{ padding: '6px', textAlign: 'right', color: '#9CA3AF' }}>{Math.round(m.pyHours * 100) / 100}h</td>
                                  <td style={{ padding: '6px', textAlign: 'center', color: '#9CA3AF', fontSize: 11 }}>{m.weekCount ? `${m.weekCount}wk` : '—'}</td>
                                  <td style={{ padding: '6px', textAlign: 'right' }}>
                                    <input type="number" min="0" max="999" step="0.5" value={sel.hours} disabled={!sel.checked}
                                      onChange={e => setRfSelected(prev => ({ ...prev, [key]: { ...sel, hours: parseFloat(e.target.value) || 0 } }))}
                                      style={{ width: 55, padding: '3px 4px', borderRadius: 4, border: '1px solid #E5E7EB', textAlign: 'center', fontSize: 12 }} />
                                  </td>
                                  <td style={{ padding: '6px', textAlign: 'center' }}>
                                    <input type="number" min="1" max="52" step="1" value={sel.weeks || 1} disabled={!sel.checked}
                                      onChange={e => setRfSelected(prev => ({ ...prev, [key]: { ...sel, weeks: parseInt(e.target.value) || 1 } }))}
                                      style={{ width: 42, padding: '3px 4px', borderRadius: 4, border: '1px solid #E5E7EB', textAlign: 'center', fontSize: 12 }} />
                                  </td>
                                  <td style={{ padding: '6px', textAlign: 'center' }}>
                                    <input type="number" min="0" max="52" step="1" value={sel.offsetWeeks || 0} disabled={!sel.checked}
                                      onChange={e => setRfSelected(prev => ({ ...prev, [key]: { ...sel, offsetWeeks: parseInt(e.target.value) || 0 } }))}
                                      style={{ width: 42, padding: '3px 4px', borderRadius: 4, border: '1px solid #E5E7EB', textAlign: 'center', fontSize: 12 }} />
                                  </td>
                                  <td style={{ padding: '6px', textAlign: 'right', fontSize: 11, fontWeight: 600, color: tight === 'short' ? '#DC2626' : tight === 'tight' ? '#D97706' : '#374151' }}
                                    title="Average per week. Hours are placed day by day in 0.25h units around this person's capacity.">
                                    {sel.checked ? `${perWeekApprox ? '~' : ''}${hrsPerWeek}h` : '—'}
                                  </td>
                                  <td style={{ padding: '6px', textAlign: 'right', fontSize: 11, whiteSpace: 'nowrap' }}>
                                    {!p ? <span style={{ color: '#9CA3AF' }}>&mdash;</span> : (
                                      <>
                                        <div style={{ color: sel.checked ? '#374151' : '#9CA3AF' }}>{roundQ(p.freeHours)}h free</div>
                                        {tight === 'short' && (
                                          <div style={{ color: '#DC2626', fontWeight: 700 }}>
                                            {roundQ(p.unassigned)}h won&rsquo;t fit
                                            {p.fitWeeks ? (
                                              <button type="button"
                                                onClick={() => setRfSelected(prev => ({ ...prev, [key]: { ...sel, weeks: p.fitWeeks } }))}
                                                title={`Spread over ${p.fitWeeks} weeks instead of ${sel.weeks || 1} — the shortest window that fits all ${sel.hours}h`}
                                                style={{ display: 'block', marginLeft: 'auto', marginTop: 2, fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 4, border: '1px solid #FCA5A5', background: '#FEF2F2', color: '#B91C1C', cursor: 'pointer' }}>
                                                Extend to {p.fitWeeks} wks
                                              </button>
                                            ) : (
                                              <div style={{ fontWeight: 400, color: '#B91C1C' }}>no room within 52 wks</div>
                                            )}
                                          </div>
                                        )}
                                      </>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14, padding: '10px 0', borderTop: '1px solid #E5E7EB' }}>
                        <div style={{ fontSize: 12, color: '#6B7280' }}>
                          {checkedStaff.length} staff &middot; {Math.round(totalHrs * 100) / 100}h total
                          {rfShortHrs > 0 && <> &middot; <strong style={{ color: '#DC2626' }}>{rfShortHrs}h won&rsquo;t fit</strong></>}
                        </div>
                        <button className="sched-assign-btn" style={{ padding: '8px 20px', fontSize: 13 }} disabled={!rfWeek || checkedStaff.length === 0} onClick={() => setRfStep(2)}>Preview Timeline &rarr;</button>
                      </div>
                    </>
                  )}

                  {rfPreview.team?.length > 0 && rfStep === 2 && (
                    <>
                      {overDeadline && (
                        <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 12, color: '#92400E' }}>
                          &#9888; Projected finish exceeds deadline by <strong>{Math.ceil(projectedFinishWk - deadlineWk)} week(s)</strong>. Adjust hours, weeks, or offsets to fit within the deadline.
                        </div>
                      )}
                      {rfShort.length > 0 && (
                        <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 12, color: '#991B1B' }}>
                          &#9888; <strong>{rfShortHrs}h won&rsquo;t fit</strong> in the weeks chosen &mdash; {rfShort.map(r => `${r.name} (${roundQ(r.unassigned)}h)`).join(', ')}.
                          Those hours will <strong>not</strong> be scheduled. Go back to extend their weeks, or schedule someone else for the rest.
                        </div>
                      )}

                      <div className="rf-gantt-container" style={{ maxHeight: 360, overflowY: 'auto' }}>
                        <div className="rf-gantt-header">
                          <div className="rf-gantt-label" style={{ background: '#F9FAFB', borderRight: '1px solid #E5E7EB' }}>Staff</div>
                          {Array.from({ length: Math.ceil(totalWks) }, (_, i) => {
                            const wkDate = new Date(rfWeek); wkDate.setDate(wkDate.getDate() + i * 7);
                            return <div key={i} className="rf-gantt-week-col" style={{ width: 70 }}>{fmtShortDate(wkDate.toISOString())}</div>;
                          })}
                        </div>

                        {ganttBars.map(bar => {
                          const barLeft = (bar.startWk / totalWks) * (totalWks * 70);
                          const barWidth = Math.max((bar.weeks / totalWks) * (totalWks * 70), 20);
                          const color = bar.unassigned ? '#DBE7EE' : primaryRoleColor(bar.roles);
                          const hrsWk = Math.round((bar.hours / bar.weeks) * 4) / 4;
                          const short = rfShort.find(r => r.key === bar.key);
                          return (
                            <div key={bar.key} className="rf-gantt-row">
                              <div className="rf-gantt-label" title={bar.unassigned ? `Placeholder · ${bar.level || '?'}` : `${bar.name} (${bar.level})`}>
                                {bar.unassigned ? `Placeholder · ${bar.level || '?'}` : bar.name}
                              </div>
                              <div className="rf-gantt-track" style={{ width: totalWks * 70 }}>
                                <div className="rf-gantt-bar" style={{ left: barLeft, width: barWidth, background: color, ...(bar.unassigned ? { border: '1.5px dashed #003D4C', color: '#003D4C' } : {}), ...(short ? { outline: '2px solid #DC2626', outlineOffset: 1 } : {}) }}
                                  title={short
                                    ? `${roundQ(bar.hours - short.unassigned)}h of ${bar.hours}h fit over ${bar.weeks}wk — ${roundQ(short.unassigned)}h won't be scheduled`
                                    : `${bar.hours}h over ${bar.weeks}wk (${hrsWk}h/wk)`}>
                                  {barWidth > 50 ? (short ? `${roundQ(bar.hours - short.unassigned)}h` : `${bar.hours}h`) : ''}
                                </div>
                              </div>
                            </div>
                          );
                        })}

                        {deadlineWk != null && deadlineWk > 0 && (
                          <div className="rf-gantt-deadline" style={{ left: 160 + (deadlineWk / totalWks) * (totalWks * 70) }}>
                            <div className="rf-gantt-deadline-label">Due {fmtShortDate(dueDate)}</div>
                          </div>
                        )}
                      </div>

                      <div style={{ display: 'flex', gap: 16, marginTop: 10, fontSize: 12, color: '#6B7280' }}>
                        <span>Projected finish: <strong style={{ color: overDeadline ? '#DC2626' : '#059669' }}>
                          {rfWeek ? fmtShortDate(new Date(new Date(rfWeek).getTime() + projectedFinishWk * 7 * 86400000).toISOString()) : '—'}
                        </strong></span>
                        <span>{checkedStaff.length} staff &middot; {rfShortHrs > 0 ? <>{rfPlacedHrs}h of {Math.round(totalHrs * 100) / 100}h fit</> : <>{Math.round(totalHrs * 100) / 100}h total</>}</span>
                      </div>

                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14, padding: '10px 0', borderTop: '1px solid #E5E7EB' }}>
                        <button className="sched-mgr-pill" style={{ fontSize: 12 }} onClick={() => setRfStep(1)}>&larr; Back to Configure</button>
                        <button className="sched-assign-btn" style={{ padding: '8px 20px', fontSize: 13 }}
                          disabled={rfSaving || rfNeedsLevel}
                          title={rfNeedsLevel ? 'Pick a level for each checked placeholder row first' : undefined}
                          onClick={async () => {
                            setRfSaving(true);
                            try {
                              const entries = [];
                              const placeholders = [];
                              for (const m of (rfPreview?.team || [])) {
                                const key = m.staffId || m.peStaffIndex;
                                const sel = rfSelected[key];
                                if (!sel || !sel.checked || !sel.hours || sel.hours <= 0) continue;
                                const numWeeks = Math.max(1, sel.weeks || 1);
                                const hours = Math.round(sel.hours * 4) / 4;
                                if (!rfIsUnassigned(m)) {
                                  // Exactly what the Room column previewed — the capacity plan,
                                  // not a flat Mon–Fri split.
                                  for (const [date, h] of Object.entries(rfPlan[key]?.dailyHours || {})) {
                                    entries.push({ staffId: m.staffId, date, hours: h });
                                  }
                                } else {
                                  const level = rfNeedsPicker(m) ? sel.phLevel : m.level;
                                  if (!level) continue;
                                  const start = new Date(rfWeek); start.setDate(start.getDate() + (sel.offsetWeeks || 0) * 7);
                                  const end = new Date(start); end.setDate(end.getDate() + numWeeks * 7 - 3);
                                  placeholders.push({ departmentCode: rfPhDept(m), level, hours, startDate: start.toISOString().split('T')[0], endDate: end.toISOString().split('T')[0] });
                                }
                              }
                              await rollForwardAPI({ targetJobId: selectedJob.JobId, confirm: true, entries, placeholders, createdBy: username });
                              refreshNotifications?.();
                              await refreshSchedule();
                              setRfOpen(false);
                              setRfPreview(null);
                            } catch (e) { alert('Roll forward failed: ' + e.message); }
                            finally { setRfSaving(false); }
                          }}>
                          {rfSaving ? 'Scheduling...' : rfShortHrs > 0 ? `Schedule ${rfPlacedHrs}h (${rfShortHrs}h won't fit)` : 'Confirm & Schedule'}
                        </button>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        );
      })()}

      {/* ── Link Job Modal ── */}
      {linkingJob && selectedJob && (() => {
        const filteredLinkJobs = enrichedJobs
          .filter(j => j.JobId !== selectedJob.JobId)
          .filter(j => {
            if (!linkSearch.trim()) return false;
            const q = linkSearch.toLowerCase();
            return (j.ClientName || '').toLowerCase().includes(q) || (j.Name || '').toLowerCase().includes(q) || (j.Code || '').toLowerCase().includes(q);
          })
          .slice(0, 50);
        const sameClientJobs = enrichedJobs
          .filter(j => j.ClientId && j.ClientId === selectedJob.ClientId && j.JobId !== selectedJob.JobId)
          .filter(j => !selectedDeps.some(d => d.PredecessorJobId === j.JobId || d.DependentJobId === j.JobId));
        const renderJobRow = (j) => {
          const isSelected = String(j.JobId) === linkTarget;
          const typeColor = JOB_TYPE_COLORS[j.ServiceDesc] || JOB_TYPE_COLORS[j.JobType] || { bg: '#E5E7EB', text: '#374151' };
          return (
            <div key={j.JobId} onClick={() => setLinkTarget(String(j.JobId))}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderBottom: '1px solid #F3F4F6', cursor: 'pointer', background: isSelected ? '#DBEAFE' : 'transparent' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{j.Name}</div>
                <div style={{ fontSize: 11, color: '#6B7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{j.ClientName}{j.ClientCode ? ` · ${j.ClientCode}` : ''}</div>
              </div>
              <span className="sched-type-tag" style={{ background: typeColor.bg, color: typeColor.text, fontSize: 10, flexShrink: 0 }}>{j.ServiceDesc || j.JobType || 'General'}</span>
            </div>
          );
        };
        return (
          <div className="sched-mgr-modal-backdrop" onClick={() => setLinkingJob(false)}>
            <div className="sched-mgr-modal" style={{ width: 480 }} onClick={e => e.stopPropagation()}>
              <div className="sched-mgr-modal-header">
                <h3>Link Job Dependency</h3>
                <button className="sched-mgr-sidebar-close" onClick={() => setLinkingJob(false)}>&times;</button>
              </div>
              <div className="sched-mgr-modal-job">
                <span className="sched-mgr-modal-job-name">{selectedJob.Name}</span>
                <span className="sched-mgr-modal-job-client">{selectedJob.ClientName}</span>
                <span className="sched-mgr-modal-job-budget" style={{ marginTop: 4 }}>This job cannot start until the selected predecessor is complete.</span>
              </div>
              <input type="text" placeholder="Search by client or job name..." value={linkSearch}
                onChange={e => { setLinkSearch(e.target.value); setLinkTarget(''); }} className="sched-modal-job-search" style={{ marginBottom: 10 }} autoFocus />
              {!linkSearch.trim() && sameClientJobs.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#0369A1', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 6 }}>Suggested &mdash; Same Client ({sameClientJobs.length})</div>
                  <div style={{ maxHeight: 240, overflowY: 'auto', border: '1px solid #BAE6FD', borderRadius: 8, background: '#F0F9FF' }}>{sameClientJobs.map(j => renderJobRow(j))}</div>
                </div>
              )}
              {linkSearch.trim() && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 6 }}>Search Results ({filteredLinkJobs.length})</div>
                  <div style={{ maxHeight: 280, overflowY: 'auto', border: '1px solid #E5E7EB', borderRadius: 8 }}>
                    {filteredLinkJobs.length === 0 ? <div style={{ padding: 16, textAlign: 'center', color: '#9CA3AF', fontSize: 12 }}>No jobs match your search.</div> : filteredLinkJobs.map(j => renderJobRow(j))}
                  </div>
                </div>
              )}
              {linkTarget && (() => {
                const sel = enrichedJobs.find(j => String(j.JobId) === linkTarget);
                if (!sel) return null;
                const typeColor = JOB_TYPE_COLORS[sel.ServiceDesc] || JOB_TYPE_COLORS[sel.JobType] || { bg: '#E5E7EB', text: '#374151' };
                return (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: '#D1FAE5', borderRadius: 8, marginBottom: 12 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#065F46' }}>{sel.Name}</div>
                      <div style={{ fontSize: 11, color: '#047857' }}>{sel.ClientName} &rarr; {selectedJob.Name}</div>
                    </div>
                    <span className="sched-type-tag" style={{ background: typeColor.bg, color: typeColor.text, fontSize: 10, flexShrink: 0 }}>{sel.ServiceDesc || sel.JobType || 'General'}</span>
                  </div>
                );
              })()}
              <button className="sched-assign-btn sched-mgr-modal-add" disabled={!linkTarget}
                onClick={async () => {
                  await addDependency(parseInt(linkTarget), selectedJob.JobId);
                  await refreshDeps();
                  setLinkingJob(false); setLinkTarget(''); setLinkSearch('');
                }}>Confirm Link</button>
            </div>
          </div>
        );
      })()}

      {/* Send to Pool — one instance shared by the row button and the sidebar
          action, so both go through the same confirm gate. On success we patch the
          job locally with its new pool membership: in My Jobs that makes it drop
          out of the list immediately (the whole point of pooling), and a
          refreshSchedule follows to pick up any cleared entries. */}
      {poolJob && (
        <SendToPoolModal
          job={poolJob}
          onClose={() => setPoolJob(null)}
          onSent={(res) => {
            patchJob(poolJob.JobId, {
              PoolJobId: res.poolJobId,
              PoolCode: res.poolCode,
              PooledAt: res.pooledAt,
            });
            if (selectedJobId === poolJob.JobId) setSelectedJobId(null);
            if (res.cleared?.deleted) refreshSchedule?.();
            refreshNotifications?.();
          }}
        />
      )}
    </div>
  );
}
